import { Button, Col, Drawer, Row, Table, Tag } from 'antd';
import React from 'react';
import { useState, useEffect } from 'react';
import PageTitle from 'shared_components/page_title';
import DownloadCSV from 'shared_components/download_csv';
import { fetchWardAndRoomsItems, applySorting } from 'services/settings/lookup_table/wardsAndRooms.jsx';
import WardAndRoomsForm from './wardAndRoomsForm';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import DragableBodyRow from 'shared_components/drag_and_drop_row';
import { showMessage } from 'services/settings/message';

function WardsAndRooms() {
	const [wardAndRooms, setWardAndRooms] = useState([]);
	const [hasReordered, setHasReordered] = useState(false);
	const [sortedInfo, setSortedInfo] = useState({});
	const [drawerState, setDrawerState] = useState({
		visible: false,
		drawerTitle: '',
		selecetedData: null,
		buttonNames: '',
	});

	useEffect(() => {
		getWardAndRoom();
	}, []);

	const onSuccessCreate = async () => {
		onDrawerClose();
		await getWardAndRoom();
		showMessage('M0002');
	};

	const onSuccessUpdate = async () => {
		onDrawerClose();
		await getWardAndRoom();
		showMessage('M0003');
	};

	const getWardAndRoom = async () => {
		const data = await fetchWardAndRoomsItems();
		// Sort by locationSort ASC, but put records without locationSort at the end
		const sortedData = (data || []).sort((a, b) => {
			if (a.locationSort != null && b.locationSort != null) {
				return a.locationSort - b.locationSort;
			}
			if (a.locationSort == null) return 1;
			if (b.locationSort == null) return -1;
			return 0;
		});
		setWardAndRooms(sortedData);
	};

	const applySort = async () => {
		const dataPayload = wardAndRooms.map((rec, index) => {
			return {
				locationID: rec.locationID,
				locationSort: index + 1,
			};
		});

		await applySorting(dataPayload);
		setHasReordered(false);
	};

	const moveRow = (dragIndex, hoverIndex) => {
		const dragRow = wardAndRooms[dragIndex];

		const updatedData = update({ wardAndRooms }, {
			wardAndRooms: { $splice: [[dragIndex, 1], [hoverIndex, 0, dragRow]] }
		});

		setWardAndRooms(updatedData.wardAndRooms);
		setHasReordered(true);
	};

	const showDrawer = record => {
		setDrawerState({
			visible: true,
			drawerTitle: 'ADD WARD AND ROOMS',
			selecetedData: null,
			buttonNames: 'ADD',
		});
	};

	const displayDrawer = record => {
		setDrawerState({
			visible: true,
			drawerTitle: 'UPDATE WARD AND ROOMS',
			selecetedData: record,
			buttonNames: 'UPDATE',
		});
	};

	const onDrawerClose = () => {
		setDrawerState({
			visible: false,
			drawerTitle: '',
			selecetedData: null,
			buttonNames: '',
		});
	};

	const handleTableChange = (pagination, filters, sorter) => {
		setSortedInfo(sorter);
	};

	const components = {
		body: {
			row: DragableBodyRow,
		},
	};

	const columns = [
		{
			title: 'NAME',
			dataIndex: 'name',
			key: 'name',
			width: 300,
			sorter: (a, b) => { return a.name.localeCompare(b.name) },
			sortDirections: ['ascend', 'descend'],
		},
		{
			title: 'DEPARTMENT',
			dataIndex: 'department',
			width: 200,
		},
		{
			title: 'DATE CREATED',
			dataIndex: 'created_date',
			width: 200,
			render: date => date ? new Date(date).toLocaleDateString() : '',
		},
		{
			title: 'STATUS',
			dataIndex: 'active',
			width: 100,
			render: active => (
				<Tag color={active === 0 ? 'red' : 'green'}>{active === 0 ? 'INACTIVE' : 'ACTIVE'}</Tag>
			),
		},
	];

	return (
		<>
			<Row justify="end" style={{ marginTop: 12, marginBottom: 12, display: 'flex', gap: '10px' }}>
				<DownloadCSV
					confirmRequired={true}
					isLookupTable={true}
					confirmDetails={{
						section: 'WARDS AND ROOMS',
						count: wardAndRooms.length
					}}
					data={wardAndRooms}
					filename="wards_and_rooms"
					columns={[
						{ title: 'NAME', dataIndex: 'name' },
						{ title: 'DEPARTMENT', dataIndex: 'department' },
						{ 
							title: 'DATE CREATED', 
							dataIndex: 'created_date',
							render: date => date ? new Date(date).toLocaleDateString() : ''
						},
						{ 
							title: 'STATUS', 
							dataIndex: 'active',
							render: active => active === 0 ? 'INACTIVE' : 'ACTIVE'
						}
					]}
				/>
				<Button onClick={applySort} shape="round" type="primary" disabled={!hasReordered}>
					APPLY SORTING
				</Button>
				<Button
					// disabled={buttonDisable}
					type="primary"
					shape="round"
					onClick={showDrawer}
					style={{ marginRight: '15px' }}
					// icon={<PlusOutlined />}
				>
					ADD WARD/ROOM
				</Button>
				{/* <TablePager handleChange={this.handleChange} /> */}
			</Row>
			<DndProvider backend={HTML5Backend}>
				<Table
					dataSource={wardAndRooms}
					columns={columns}
					components={components}
					pagination={false}
					rowKey={record => record.locationID}
					onChange={handleTableChange}
					onRow={(record, index) => ({
						index: wardAndRooms.findIndex(item => item.locationID === record.locationID),
						moveRow: moveRow,
						onDoubleClick: () => {
							displayDrawer(record);
						},
					})}
				/>
			</DndProvider>

			<Drawer
				title={drawerState.drawerTitle}
				width="30%"
				visible={drawerState.visible}
				onClose={onDrawerClose}
				destroyOnClose
			>
				<WardAndRoomsForm
					buttonNames={drawerState.buttonNames}
					onDrawerClose={onDrawerClose}
					selecetedData={drawerState.selecetedData}
					onSuccessCreate={onSuccessCreate}
					onSuccessUpdate={onSuccessUpdate}
					maxLocationSort={Math.max(...wardAndRooms.map(w => w.locationSort || 0), 0)}
				/>
			</Drawer>
		</>
	);
}

export default WardsAndRooms;
